import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import { useParams, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Typography, Box, Grid, ListItem } from '@mui/material'
import { Checkbox, FormControlLabel } from '@mui/material'

import { PageDisplayer, CardCheckList, TextInput } from '../../../../ui'
import { useTariffCalls, useZonesReducer } from '../../hooks'
import { tariffNewStyles } from './tariff-zone.style'

const PREFIX = 'TariffZones'

const classes = {
  root: `${PREFIX}-root`,
  checked: `${PREFIX}-checked`,
  colorPanel: `${PREFIX}-colorPanel`,
  provinciaLine: `${PREFIX}-provinciaLine`,
  provinciaLineChosen: `${PREFIX}-provinciaLineChosen`,
  zipContainer: `${PREFIX}-zipContainer`,
  zipCard: `${PREFIX}-zipCard`,
  zipCardChosen: `${PREFIX}-zipCardChosen`,
  zipMarker: `${PREFIX}-zipMarker`,
}

const StyledPageDisplayer = styled(PageDisplayer)(({ theme }) => ({
  [`& .${classes.root}`]: {
    color: '#fff',
    '&$checked': { color: '#fff' },
  },
  [`& .${classes.checked}`]: {},
  [`& .${classes.colorPanel}`]: {
    backgroundColor: '#fafafa',
  },
  [`& .${classes.provinciaLine}`]: {
    cursor: 'pointer',
  },
  [`& .${classes.provinciaLineChosen}`]: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  [`& .${classes.zipContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    maxHeight: '60vh',
    overflow: 'auto',
  },
  [`& .${classes.zipCard}`]: {
    margin: '0.5em',
    backgroundColor: 'white',
    border: '1px solid black',
    borderRadius: '5px',
    padding: '1em',
    pointerEvents: 'none',
  },
  [`& .${classes.zipCardChosen}`]: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  [`& .${classes.zipMarker}`]: {
    '&:first-child': {
      display: 'block',
      width: '1px',
      padding: '0',
      '& svg': {
        display: 'none',
      },
    },
  },
}))

export const TariffZones = ({}) => {
  //Tools
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { getTariff, getTariffZone } = useTariffCalls()
  const { comunidad, provincias, zips, zoneLoad } = useZonesReducer()

  //Available data:
  const { tariffId } = useParams()

  //Fetched data
  const [empresaId, setEmpresaId] = useState()
  const [provinciaMirada, setProvinciaMirada] = useState(null)
  const [searchQuery, setSearchQuery] = useState(null)

  useEffect(() => {
    getTariff(tariffId).then(({ data }) => {
      setEmpresaId(data.empresa_id)
    })
  }, [tariffId, getTariff, setEmpresaId])

  useEffect(() => {
    if (!empresaId) {
      return
    }
    getTariffZone(empresaId, tariffId).then(({ data }) => {
      setProvinciaMirada(data.codPostales[0] ? data.codPostales[0].prov : null)
      zoneLoad(data)
    })
  }, [empresaId, tariffId, getTariffZone, setProvinciaMirada, zoneLoad])

  const zip_choice = useMemo(
    () => (zips[provinciaMirada] ? zips[provinciaMirada] : []),
    [provinciaMirada, zips]
  )

  //GOING BACK
  const handleGoBack = useCallback(() => {
    navigate('/tariffs/' + tariffId)
  }, [navigate, tariffId])

  const handleCallGoBack = useCallback(() => {
    handleGoBack()
  }, [handleGoBack])

  //CHANGE PROVINCE BEING BROWSED
  const handleProvinciaSelect = useCallback(
    (provincia) => {
      setSearchQuery(null)
      setProvinciaMirada(provincia)
    },
    [setSearchQuery, setProvinciaMirada]
  )

  //CHANGE SEARCH QUERY
  const handleSearchQueryChange = useCallback(
    (event) => {
      const newText = event.target.value
      setSearchQuery(newText)
    },
    [setSearchQuery]
  )

  //Auxiliary components:
  const ChamaleonCheckbox = useCallback((props) => <Checkbox {...props} />, [])
  const CaseCheckbox = useCallback(({ provinciaMirada, element, ...props }) => {
    if (provinciaMirada === element.name) return <ChamaleonCheckbox {...props} />
    return <Checkbox {...props} />
  }, [])
  const provinceCheckRender = useCallback(
    (element) => {
      const isSelected = element.name === provinciaMirada
      return (
        <ListItem
          key={element.name}
          onClick={() => handleProvinciaSelect(element.name)}
          sx={{
            cursor: 'pointer',
            backgroundColor: isSelected ? 'primary.main' : 'transparent',
            color: isSelected ? 'white' : 'inherit',
            '&:hover': {
              backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          <CaseCheckbox
            provinciaMirada={provinciaMirada}
            element={element.name}
            color="primary"
            name={element.name}
            checked={element.chosen === element.size}
            indeterminate={element.chosen < element.size && element.chosen > 0}
            // onChange={handleProvinciaCheck}
            disableRipple
            sx={{
              color: isSelected ? 'white' : 'inherit',
            }}
          />
          {element.name}
        </ListItem>
      )
    },
    [provinciaMirada, handleProvinciaSelect]
  )
  const zipCheckRender = useCallback(
    (element) => (
      <Box
        key={element.value}
        sx={{
          margin: '0.5em',
          backgroundColor: element.activa ? 'primary.main' : 'white',
          color: element.activa ? '#fff' : 'inherit',
          border: '1px solid',
          borderColor: element.activa ? 'primary.main' : 'black',
          borderRadius: '5px',
          padding: '1em',
          pointerEvents: 'none',
          cursor: 'default',
          minWidth: '80px',
          textAlign: 'center',
          fontWeight: element.activa ? 500 : 400,
        }}
      >
        {element.value}
      </Box>
    ),
    []
  )
  const zipBlockRender = useCallback(
    ({ children }) => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-evenly',
          maxHeight: '60vh',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    ),
    []
  )

  const communityField = useMemo(() => {
    return {
      value: comunidad,
      name: 'comunidad',
      readOnly: true,
    }
  }, [comunidad])

  //Main component
  return (
    <StyledPageDisplayer
      loading={!zips || !zip_choice.length}
      error={false /*Canviar? */}
      handleGoBack={handleCallGoBack}
      textGoBack={formatMessage({ id: 'pages.tariffs.zone.back' })}
    >
      <Typography variant="h4" color="textSecondary">
        {formatMessage({ id: 'pages.tariffs.zone.title' })}
      </Typography>
      <Box mt={3}>
        <Grid container spacing={5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" gutterBottom>
              {formatMessage({ id: 'pages.tariffs.zone.cc_aa' })}
            </Typography>
            <TextInput {...communityField} />
          </Grid>
        </Grid>
      </Box>
      <Box mt={3}>
        <Grid container spacing={5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" gutterBottom>
              {formatMessage({ id: 'pages.tariffs.zone.provinces.title' })}
            </Typography>
            <CardCheckList
              className={classes.colorPanel}
              keyField="name"
              nameField="name"
              elements={provincias}
              selected={provincias
                .filter((provincia) => provincia.size === provincia.chosen)
                .map((provincia) => provincia.name)}
              funcRenderElement={provinceCheckRender}
              wholeSelector={false}
              wholeCaption={formatMessage({ id: 'pages.tariffs.zone.provinces.all' })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" gutterBottom>
              {formatMessage({ id: 'pages.tariffs.zone.zips.title' })}
            </Typography>
            <CardCheckList
              className={classes.colorPanel}
              keyField="value"
              nameField="value"
              elements={zip_choice}
              selected={zip_choice.filter((zip) => zip.activa).map((el) => el.value)}
              funcRenderElement={zipCheckRender}
              funcRenderContainer={zipBlockRender}
              searchBox={true}
              searchValue={searchQuery}
              searchPlaceholder={formatMessage({ id: 'pages.tariffs.zone.zips.filter' })}
              funcSearchOnChange={handleSearchQueryChange}
              wholeSelector={false}
              wholeCaption={formatMessage({ id: 'pages.tariffs.zone.zips.all' })}
              disabled
            />
          </Grid>
        </Grid>
      </Box>
    </StyledPageDisplayer>
  )
}
